import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { PORTFOLIO_COMPANIES } from "@/data/content";
import { pickLocale } from "@/i18n";

export default function PortfolioCompanies() {
  useReveal();
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />
      <PageHero
        eyebrow={t("portfolioCompanies.heroEyebrow")}
        title={t("portfolioCompanies.heroTitle")}
        accent={<><br/>{t("portfolioCompanies.heroAccent")}</>}
        sub={t("portfolioCompanies.heroSub")}
        bgImage="/assets/vzvejjze_portfolio-companies.jpg"
      />

      {/* INTRO */}
      <section className="relative bg-white py-24 lg:py-32 border-b border-[#14355a]/10">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div data-reveal className="text-center">
            <div className="eyebrow text-[#14355a]">{t("portfolioCompanies.overviewEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
                fontWeight: 500,
                lineHeight: "1.1",
              }}
              className="mt-7 font-display text-[#14355a] tracking-tight"
            >
              {t("portfolioCompanies.overviewTitle")} {t("portfolioCompanies.overviewAccent")}
            </h2>
            <div className="mt-8 mx-auto w-12 h-px bg-unik-gold" />
            <p className="mt-8 max-w-3xl mx-auto text-[18px] lg:text-[19px] leading-[1.95] text-black">
              {t("portfolioCompanies.overviewBody")}
            </p>
          </div>
        </div>
      </section>

      {/* COMPANIES — modern card grid */}
      <section className="relative bg-[#f4f5f7] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {PORTFOLIO_COMPANIES.map((p, i) => (
              <Link
                key={p.slug}
                to={`/portfolio/${p.slug}`}
                data-reveal
                style={{ "--reveal-delay": `${i * 110}ms` }}
                className="group relative block bg-white border border-[#14355a]/10 overflow-hidden transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(20,53,90,0.14)] hover:border-[#14355a]/30"
                data-testid={`portfolio-card-${p.slug}`}
              >
                {/* Image header */}
                <div className="relative aspect-[16/9] overflow-hidden bg-[#0e2540]">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540]/80 via-[#0e2540]/20 to-transparent" />

                  {/* Sector tag + number */}
                  <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                    <span className="inline-block px-3.5 py-1.5 bg-white/95 backdrop-blur-sm text-[#14355a] text-[10.5px] uppercase tracking-[0.22em] font-medium">
                      {pickLocale(p, "sector")}
                    </span>
                    <span
                      className="font-display text-white/90 tracking-[0.1em]"
                      style={{ fontSize: "clamp(1.25rem, 1.4vw, 1.5rem)", fontWeight: 500, lineHeight: 1 }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 lg:p-10">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">
                    {pickLocale(p, "focus")}
                  </div>
                  <h3
                    style={{
                      fontSize: "clamp(1.75rem, 2.4vw, 2.25rem)",
                      fontWeight: 500,
                      lineHeight: 1.1,
                    }}
                    className="mt-4 font-display text-[#14355a] tracking-tight"
                  >
                    {p.name}
                  </h3>
                  <div className="mt-5 w-10 h-px bg-[#14355a]/20" />
                  <p className="mt-6 text-[16px] lg:text-[17px] text-black leading-[1.8]">
                    {(pickLocale(p, "body") || p.body)[0]}
                  </p>

                  <div className="mt-8 inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-[#14355a] font-display">
                    {t("common.learnMore")}
                    <ArrowUpRight
                      size={18}
                      className="text-[#14355a]/50 group-hover:text-[#14355a] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("portfolioCompanies.leadershipEyebrow")}
        title={t("portfolioCompanies.leadershipTitle")}
        accent={<><br/>{t("portfolioCompanies.leadershipAccent")}</>}
        body={t("portfolioCompanies.leadershipBody")}
        primary={{ label: t("nav.aboutMenu.leadership"), to: "/about/leadership" }}
        secondary={{ label: t("common.contactUs"), to: "/contact" }}
      />
      <SiteFooter />
    </div>
  );
}
